import * as d3 from 'd3';
import * as _ from 'lodash';

import { IChartOptions } from '../../models/IChartOptions';
import { SvgManager } from '../SvgManager';
import { IChart } from '../interfaces/IChart';
import { ChartWithAxesBase } from './ChartWithAxesBase';
import { RoundingHelper, SymbolScaleHelper, NumberFormatHelper, ILogger, LoggerFactory, GuidHelper } from '../../helpers/helpers';
import { ShapeRenderer } from '../pieces/Shape';
import { ShapeFormat } from '../../models';
import { notNaN } from '../../helpers/RoundingHelper';
import { whiteTextColor } from '../../../common/constants';

export interface IScatterPlot extends IChart {

}

export class ScatterPlot extends ChartWithAxesBase implements IScatterPlot {
    private _crosshairClipPathId: string;
    private _crosshairDisabled: boolean = false;
    private _crosshairGroup: d3.Selection<any>;
    private _crosshairHeight: number;
    private _crosshairWidth: number;
    private _data: any[];
    private _dateFormatter: d3.time.Format;
    private _isZooming: boolean;
    private _logger: ILogger;
    private _roundingHelper: RoundingHelper;
    private _shapeRenderer: ShapeRenderer;
    private _shapes: ShapeFormat[];
    private _symbolScale = 0.5;
    private _textHeight = 17;
    private _unzoomedData: any[];
    private _xMax: number;
    private _xMin: number;
    private _xSelectionFunction: (d) => any;
    private _yMax: number;
    private _yMin: number;
    //TODO use the zoom class
    private _zoomGroup: d3.Selection<any>;
    private _zoomRectangle: d3.Selection<any>;

    _crosshairCallback: (date: Date) => void;

    constructor(chartOptions: IChartOptions, svgManager: SvgManager) {
        super(chartOptions, svgManager, 'scatter-plot');
        try {
            this._logger = LoggerFactory.getLogger('WideTech.Charting.scatterPlot');
            this._crosshairClipPathId = `crosshairClipPath${GuidHelper.getGuid()}`;
            this._crosshairWidth = 200;
            this._crosshairHeight = 20;
            this._unzoomedData = [];
            const container = super.plotAreaGroup();
            const shapeContainer = container
                .insert('g', '.series-group')
                .classed('shapes', true)
                .style('clip-path', container.select('.series-group').style('clip-path'));
            this._shapeRenderer = new ShapeRenderer(shapeContainer)
                .xScale(this.xAxis().scale())
                .yScale(this.yAxis().scale());

            this.initCrosshair(container);
            this.initZoom(container);
            this.resizeFunction(this.applyResize());
            this.isTitleVisible(true);
            this.isLegendVisible(true);
            this.abscissValueSelector((d) => d !== null && d !== undefined ? d.value : null);
            this._dateFormatter = d3.time.format('%Y-%m-%d %H:%M');
            this._roundingHelper = new RoundingHelper();
        } catch (e) {
            this._logger.error(e);
        }
    }

    update(data: any[]) {
        const self = this;
        this._data = data;
        this.executeHandled(() => {
            if (self._data) {
                if (self._data.length > 0) {
                    let displayOptions = this.displayOptions();
                    if (!displayOptions) {
                        displayOptions = {};
                        this.displayOptions(displayOptions);
                    }
                    this.updateXAxisFormat(displayOptions);
                    this.updateYAxisFormat(displayOptions);
                }
                this.series()
                    .forEach(s => {
                        const values = self._data
                            .filter(d => {
                                const yValue = _.find(d.y, v => s.selector()(v) === s.identifier());
                                return !!yValue || yValue === 0;
                            })
                            .map(d => {
                                const yValue = _.find(d.y, v => s.selector()(v) === s.identifier());
                                return {
                                    timeStamp: d.timeStamp,
                                    x: self.getXValue(d),
                                    y: d.y,
                                    value: s.value(yValue)
                                };
                            });

                        const g = self.seriesGroup()
                            .select(`#seriesId${s.identifier()}`)
                            // in a scatter plot symbol are always visible ? don't see the point
                            // or not apparently
                            .classed('hidden-group', !s.isSymbolVisible());
                        const selection = g.selectAll('.scatter-series')
                            .data(values);

                        selection.enter()
                            .append('path')
                            .classed('scatter-series', true)
                            .style('stroke', s.color());

                        selection
                            .attr({
                                'transform': (d, i) => {
                                    const x = notNaN(self.xAxis().scale()(d.x), 0);
                                    const y = notNaN(self.yAxis().scale()(d.value), 0);
                                    return `translate(${x},${y}) scale(${self._symbolScale * (this.ratio() || 1)})`;
                                },
                                'd': s.symbol()
                            })
                            .style({ 'fill': s.color() });

                        if (s.isSymbolVisible() && !this._crosshairDisabled) {
                            selection
                                .on('mouseenter', self.onMouseEnter())
                                .on('mousemove', self.onMouseMove())
                                .on('mouseout', self.onMouseOut());
                        }

                        selection.exit().remove();
                    });

                this.refreshCrosshair();
            }
        });
    }

    private updateXAxisFormat(displayOptions) {
        const self = this;
        let roundedMin = this._xMin;
        let roundedMax = this._xMax;
        const hasMin = this._xMin || this._xMin === 0;
        const hasMax = this._xMax || this._xMax === 0;
        if (!hasMin || !hasMax) {
            const roundedX = this.computeXExtent(self);
            roundedMin = hasMin ? this._xMin : roundedX.min;
            roundedMax = hasMax ? this._xMax : roundedX.max;
        }
        displayOptions.xAxisFormat = NumberFormatHelper.getFormatString(roundedMin, roundedMax);
        this.updateXAxis(roundedMin, roundedMax);
        this._shapeRenderer.xScale(this.xAxis().scale());
    }

    private computeXExtent(self: this) {
        let xmin = self._data && self._data.length > 0
            ? d3.min(self._data, d => self.getXValue(d))
            : 0;

        let xmax = self._data && self._data.length > 0
            ? d3.max(self._data, d => self.getXValue(d))
            : 1;

        if (this._shapes && this._shapes.length) {
            const x = _.flatten(this._shapes.map(s => s.points))
                .map(d => d.x)
                .filter(d => d !== null);
            if (x.length) {
                xmin = Math.min(xmin, d3.min(x));
                xmax = Math.max(xmax, d3.max(x));
            }
        }

        return this._roundingHelper.round(xmin, xmax);
    }

    private updateYAxisFormat(displayOptions): void {
        const min = this._yMin;
        const max = this._yMax;
        let roundedMin = min;
        let roundedMax = max;

        const hasMin = min || min === 0;
        const hasMax = max || max === 0;
        if (!hasMin || !hasMax) {
            const rounded = this.computeYExtent();
            roundedMin = hasMin ? min : rounded.min;
            roundedMax = hasMax ? max : rounded.max;
        }
        displayOptions.yAxisFormat = NumberFormatHelper.getFormatString(roundedMin, roundedMax);
        this.updateYAxis(roundedMin, roundedMax);
        this._shapeRenderer.yScale(this.yAxis().scale());
    }

    private computeYExtent() {
        let ymin = null;
        let ymax = null;

        const flattened = _.chain(this._data)
            .map(d => d.y)
            .flatten();
        this.series()
            .filter(s => !s.isXAxis())
            .forEach(s => {
                const values = flattened
                    .filter(d => (s.selector()(d) === s.identifier()))
                    .value();
                if (values.length > 0) {
                    const localMin = d3.min(values, v => s.value(v));
                    const localMax = d3.max(values, v => s.value(v));
                    ymin = Math.min(localMin, ymin);
                    ymax = Math.max(ymax, localMax);
                }
            });

        if (this._shapes && this._shapes.length) {
            const y = _.flatten(this._shapes.map(s => s.points))
                .map(d => d.y)
                .filter(d => d !== null);
            if (y.length) {
                ymin = Math.min(ymin, d3.min(y));
                ymax = Math.max(ymax, d3.max(y));
            }
        }
        if (ymin == null && ymax == null) {
            ymin = 0;
            ymax = 1;
        }
        return this._roundingHelper.round(ymin, ymax);
    }

    abscissValueSelector(value?): any {
        if (value) {
            this._xSelectionFunction = value;
        }
        return this._xSelectionFunction;
    }

    updateSeries(newSeriesList: any[]) {
        try {
            super.updateSeries(newSeriesList);
            this.update(this._data);
        } catch (e) {
            console.error(e);
        }
    }

    resetZoom() {
        const unzoomed = this._unzoomedData.pop();
        if (unzoomed) {
            const xScale = this.xAxis().scale();
            const yScale = this.yAxis().scale();
            // X Axis is inverted (min > max)
            if (unzoomed.xDomain[0] > unzoomed.xDomain[1]) {
                this.updateXAxis(d3.max(unzoomed.xDomain), d3.min(unzoomed.xDomain));
            } else {
                this.updateXAxis(d3.min(unzoomed.xDomain), d3.max(unzoomed.xDomain));
            }
            // Y Axis is inverted (min > max)
            if (unzoomed.yDomain[0] > unzoomed.yDomain[1]) {
                this.updateYAxis(d3.max(unzoomed.yDomain), d3.min(unzoomed.yDomain));
            } else {
                this.updateYAxis(d3.min(unzoomed.yDomain), d3.max(unzoomed.yDomain));
            }
            this.refreshSeries();
            this._shapeRenderer
                .xScale(xScale)
                .yScale(yScale);
        }
    }

    isZoomed() {
        return !!this._unzoomedData && this._unzoomedData.length > 0;
    }

    disableCrosshair(isDisabled: boolean) {
        this._crosshairDisabled = isDisabled;
    }

    setCrosshair(date: Date) {
        if (date && this._data) {
            const bisect = d3.bisector<any, any>(d => d.timeStamp).left;
            const matching = bisect(this._data, date);
            if (matching >= 0) {
                const d = this._data[matching];

                let sel = this.seriesGroup().selectAll('.scatter-series');
                sel.classed('highlighted', false);

                sel = sel
                    .filter(dt => (d && dt) ? dt.y === d.y : false)
                    .classed('highlighted', true);

                const translateX = this.xAxis().scale()(this.getXValue(d));
                this.translateCrossHair(translateX);
                const formatter = d3.format('.2f');
                this.displayCrosshair(d, formatter);
            }

        } else {
            this._crosshairGroup.style('visibility', 'hidden');
            this.seriesGroup()
                .selectAll('.scatter-series')
                .classed('highlighted', false);
        }

    }

    //angular binding
    private _scope;

    bind(scope) {
        this._scope = scope;
    }

    private initCrosshair(container: d3.Selection<any>) {
        // whole crosshair
        this._crosshairGroup = container.append('g')
            .style('visibility', 'hidden')
            .classed('crosshair-group', true);

        // clippath to have same render
        this._crosshairGroup
            .append('defs')
            .append('clipPath')
            .attr('id', this._crosshairClipPathId)
            .append('rect')
            .attr({
                'width': this._crosshairWidth,
                'height': this._crosshairHeight
            })
            .classed('crosshair-clippath', true);

        // crosshair legend and content
        const legend = this._crosshairGroup.append('g')
            .classed('crosshair-legend', true)
            .style('clip-path', `url("#${this._crosshairClipPathId}")`);

        legend.append('rect')
            .attr({
                'width': this._crosshairWidth,
                'height': this._crosshairHeight
            })
            .classed('crosshair-text-group', true);

        legend.append('text')
            .classed('crosshair-text', true)
            .attr({
                'x': 5,
                'y': this._textHeight
            });

        // crosshair line
        this._crosshairGroup.append('rect')
            .attr({
                'width': 1,
                'height': this.getHeight()
            })
            .classed('crosshair', true);
    }

    private initZoom(container: d3.Selection<any>) {
        this._zoomGroup = container
            .insert('g', '.series-group')
            .classed('zoom-zone', true);
        let xStart: number;
        let yStart: number;
        this._zoomRectangle = this._zoomGroup
            .append('rect')
            .classed('zoom-rectangle', true);
        this._zoomGroup.append('rect')
            .classed('zoom-container', true)
            .attr({
                'x': 0,
                'y': 0,
                'width': this.plotAreaWidth(),
                'height': this.plotAreaHeight()
            })
            .on('mousedown', () => {
                if (!this._isZooming) {
                    this._isZooming = true;
                    this._unzoomedData.push({
                        xDomain: this.xAxis().scale().domain(),
                        yDomain: this.yAxis().scale().domain()
                    });
                    const target = (d3.event as Event).target;
                    const position = d3.mouse(target);
                    xStart = position[0];
                    yStart = position[1];
                    this._zoomRectangle.attr({
                        'x': xStart,
                        'y': yStart
                    })
                        .style({
                            'visibility': 'visible'
                        });
                }
            })
            .on('mousemove', () => {
                if (this._isZooming) {
                    const target = (d3.event as Event).target;
                    const position = d3.mouse(target);
                    let width = position[0] - xStart;
                    let height = position[1] - yStart;
                    let x = xStart;
                    let y = yStart;
                    if (width < 0) {
                        width = Math.abs(width);
                        x = xStart - width;
                    }
                    if (height < 0) {
                        height = Math.abs(height);
                        y = yStart - height;
                    }
                    this._zoomRectangle.attr({
                        'x': x,
                        'y': y,
                        'width': width,
                        'height': height
                    });
                }
            })
            .on('mouseup', () => {
                if (this._isZooming) {
                    this._isZooming = false;
                    const target = (d3.event as Event).target;
                    const position = d3.mouse(target);
                    const x = position[0];
                    const y = position[1];
                    if (x !== xStart && y !== yStart) {
                        const xScale = this.xAxis().scale();
                        const yScale = this.yAxis().scale();
                        const xDomain = xScale.domain();
                        const yDomain = yScale.domain();
                        const x1 = xScale.invert(xStart);
                        const x2 = xScale.invert(x);
                        const y1 = yScale.invert(yStart);
                        const y2 = yScale.invert(y);
                        this._zoomRectangle
                            .attr({
                                'width': 0,
                                'height': 0
                            })
                            .style({
                                'visibility': 'hidden'
                            });
                        // X Axis is inverted (min > max)
                        if (xDomain[0] > xDomain[1]) {
                            this.updateXAxis(d3.max([x1, x2]), d3.min([x1, x2]));
                        } else {
                            this.updateXAxis(d3.min([x1, x2]), d3.max([x1, x2]));
                        }
                        // Y Axis is inverted (min > max)
                        if (yDomain[0] > yDomain[1]) {
                            this.updateYAxis(d3.max([y1, y2]), d3.min([y1, y2]));
                        } else {
                            this.updateYAxis(d3.min([y1, y2]), d3.max([y1, y2]));
                        }
                        this._shapeRenderer
                            .xScale(xScale)
                            .yScale(yScale);
                        this.refreshSeries();
                        if (this._scope) {
                            this._scope.$apply();
                        }
                    }
                }
            });
    }

    private applyResize() {
        const self = this;
        return () => {
            self.executeHandled(() => {
                self._logger.debug('Resizing.');
                self.translate();
                self.resizeAxes();
                this._shapeRenderer
                    .xScale(this.xAxis().scale())
                    .yScale(this.yAxis().scale());

                self.refreshSeries();
                self._zoomGroup.select('.zoom-container')
                    .attr({
                        'width': self.plotAreaWidth() >= 0 ? self.plotAreaWidth() : 0,
                        'height': self.plotAreaHeight() >= 0 ? self.plotAreaHeight() : 0
                    });
                self.refreshCrosshair();
                self._logger.debug('Resized.');
            });
        };
    }

    private refreshSeries() {
        if (this._data) {
            this._logger.debug('Refreshing series.');
            this._symbolScale = SymbolScaleHelper.scale(this.xAxis().scale(), this.yAxis().scale());
            this.series()
                .forEach(s => {
                    this.group()
                        .select(`#seriesId${s.identifier()}`)
                        .selectAll('.scatter-series')
                        .data(this._data)
                        .attr({
                            'transform': (d, i) => {
                                const x = this.xAxis().scale()(this.getXValue(d));
                                const yValue = _.find(d.y, v => s.selector()(v) === s.identifier());
                                let y = 0;
                                if (yValue) {
                                    y = this.yAxis().scale()(s.value(yValue));
                                }
                                return `translate(${x},${y}) scale(${this._symbolScale})`;
                            }
                        }
                        );
                });
            this._logger.debug('Series refreshed.');
        }
    }

    private refreshCrosshair() {
        this._crosshairGroup
            .select('.crosshair')
            .attr({ 'height': this.getHeight() });
        const height = (this.series().length + 1) * this._textHeight;
        this._crosshairGroup.select('.crosshair-clippath')
            .attr({ 'height': height });
        this._crosshairGroup.select('.crosshair-text-group')
            .attr({ 'height': height });
        const dataSelection = this._crosshairGroup
            .select('.crosshair-legend')
            .selectAll('.series-label')
            .data(this.series());
        dataSelection.enter()
            .append('text')
            .classed('series-label', true)
            .classed('crosshair-text', true);
        dataSelection.attr({
            'x': 5,
            'y': (d, i) => (i + 2) * this._textHeight
        });
        dataSelection.exit().remove();
    }

    private getHeight(): number {
        const h = this.plotAreaHeight();
        return h >= 0 ? h : 0;
    }

    private getXValue(d): number {
        return d !== null && d !== undefined ? this.abscissValueSelector()(d.x) : 0;
    }

    private onMouseEnter(): (d, i) => void {
        return (d, i) => {
            const formatter = d3.format('.2f');
            this.displayCrosshair(d, formatter);
            if (this._crosshairCallback) {
                this._crosshairCallback(d.timeStamp);
            }
        };
    }

    private displayCrosshair(d: { timeStamp: Date, y: any[] }, formatter: (n: number) => string) {
        if (!d) {
            return;
        }
        this._crosshairGroup
            .style('visibility', 'visible')
            .select('text')
            .text(`Time stamp: ${this._dateFormatter(d.timeStamp)}`);
        const dataSelection = this._crosshairGroup
            .selectAll('.series-label')
            .data(d.y);
        dataSelection
            .text(y => {
                const series = _.find(this.series(), s => s.identifier() === s.selector()(y));
                let label = 'Unknown';
                if (series) {
                    let value = formatter(series.value(y));
                    const crosshairFormat = series.crosshairFormat();
                    if (crosshairFormat) {
                        value = crosshairFormat(y);
                    }
                    label = series.name + ': ' + value;
                }
                return label;
            })
            .style({
                'fill': (y) => {
                    const series = _.find(this.series(), s => s.identifier() === s.selector()(y));
                    let color = whiteTextColor;
                    if (series) {
                        color = series.color();
                    }
                    return color;
                }
            });
    }

    private translateCrossHair(x: number) {
        const legendX = (x + this._crosshairWidth > this.plotAreaWidth())
            ? -this._crosshairWidth
            : 0;
        const y = 0;

        this._crosshairGroup.attr('transform', `translate(${x},${y})`);
        this._crosshairGroup.select('.crosshair-legend')
            .attr('transform', `translate(${legendX},${y})`);
    }

    private onMouseMove(): (d, i) => void {
        const self = this;
        return (d: { x: number; y: number }, i) => {
            const target = (d3.event as Event).target;
            const mouse = d3.mouse(target);
            const pos = (typeof d.x === 'number')
                ? d.x
                : (d as any).x.value;
            const translateX = RoundingHelper.notNaN(self.xAxis().scale()(pos) + mouse[0] + 2, 0);
            self.translateCrossHair(translateX);
        };
    }

    private onMouseOut(): (d, i) => void {
        const self = this;
        return (d, i) => {
            self._crosshairGroup.style('visibility', 'hidden');
            if (this._crosshairCallback) {
                this._crosshairCallback(null);
            }
        };
    }

    xAxisMin(value?: number): number {
        if (arguments.length) {
            this._xMin = value;
            this.update(this._data);
        }
        return this._xMin;
    }

    xAxisMax(value?: number): number {
        if (arguments.length) {
            this._xMax = value;
            this.update(this._data);
        }
        return this._xMax;
    }

    yAxisMin(value?: number): number {
        if (arguments.length) {
            this._yMin = value;
            this.update(this._data);
        }
        return this._yMin;
    }

    yAxisMax(value?: number): number {
        if (arguments.length) {
            this._yMax = value;
            this.update(this._data);
        }
        return this._yMax;
    }

    shapes(data: ShapeFormat[]) {
        this._shapes = data;
        this._shapeRenderer.data(data);
        this.updateXAxisFormat(this.displayOptions());
        this.updateYAxisFormat(this.displayOptions());
    }

    onCrosshair(callback: (date: Date) => void) {
        this._crosshairCallback = callback;
    }
}